import React, { FormEvent, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import queryString from 'query-string';
import { palette } from '@leafygreen-ui/palette';
import { Body, Overline } from '@leafygreen-ui/typography';
import Icon from '@leafygreen-ui/icon';

// images
import mongoCircle from './images/mongo_circle.svg';

// types
import { Search } from 'js/auth/types/search';

// utils
import colors from 'js/auth/utils/palette';
import { CloudTeams, sendError } from 'js/common/utils/bugsnag';

// services
import * as api from 'js/common/services/api';

// context
import { useAppUserParams } from 'js/auth/context/AppUserContext';
import { useRequestParams } from 'js/common/context/RequestParamsContext';

// components
import VercelPage from 'js/common/components/Vercel/VercelPage';
import VercelLayout from 'js/common/components/Vercel/VercelLayout';
import { sendAnalyticsEvents } from 'js/auth/utils/analyticsUtils';
import { useGoogleAnalytics } from 'js/auth/hooks/useGoogleAnalytics';
import { SignupMethod } from 'js/auth/types/signup';
import { useQuoraTrackingPixel } from 'js/auth/hooks/useQuoraTrackingPixel';

interface Props {
  location?: Pick<Location, 'search'>;
  windowLocation?: Pick<Location, 'assign'>;
}

const BodyText = styled(Body)`
  font-size: 16px;
  line-height: 28px;
  margin: 24px 0;
  font-weight: 400;
  a {
    color: ${colors.link};
    text-decoration: none;
  }
`;

const GraphicContainer = styled.div`
  display: grid;
  grid-template-columns: 3fr 1fr 3fr;
`;

const GraphicArrowContainer = styled.div`
  margin-top: 116px;
  padding-left: 5px;
`;

const GraphicLogo = styled.img`
  height: 80px;
  width: 80px;
  margin-bottom: 12px;
`;

const GraphicTitle = styled<React.ComponentType<Omit<React.ComponentProps<typeof Overline>, 'as'>>>(Overline)`
  color: ${palette.green.dark2};
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  margin-bottom: 5px;
`;

const GraphicName = styled(Body)`
  font-size: 16px;
  font-weight: 600;
  line-height: 28px;
  margin-bottom: 5px;
`;

const GraphicID = styled(Body)`
  color: ${palette.gray.base};
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;
  margin-bottom: 5px;
`;

const errorMessageStyle = css`
  color: ${colors.warning};
  text-align: center;
`;

export default function VercelConfirmationPage({
  location: { search } = { search: '' },
  windowLocation = window.location,
}: Props) {
  const [acknowledgeDisabled, setAcknowledgeDisabled] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState('');
  const parsedSearchLocation = queryString.parse(search) as Search;
  const {
    orgName,
    orgId,
    vercelUserId,
    vercelEmail,
    vercelTeamId,
    vercelTeamName,
    isNewVercelOrg,
    signupMethod,
    signupSource,
  } = parsedSearchLocation;
  const { centralUrl } = useRequestParams();

  const displayOrgName = (orgName as string).replace(/\+/g, ' ');
  const displayTeamName = (vercelTeamName as string)?.replace(/\+/g, ' ');

  const { appUserParams, loadAppUserParams } = useAppUserParams();

  useEffect(() => {
    loadAppUserParams();
    //TODO: CLOUDP-104556 - Remove whatever was able to be moved to the backend
    if (signupMethod !== SignupMethod.GOOGLE && signupSource) {
      sendAnalyticsEvents(search, null);
    }
  }, []);

  useQuoraTrackingPixel();
  useGoogleAnalytics(parsedSearchLocation, appUserParams);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setAcknowledgeDisabled(true);
    try {
      if (orgId && vercelUserId) {
        await api.organization.confirmVercelIntegration({
          orgId,
          vercelUserId,
          centralUrl,
        });
        windowLocation.assign(
          isNewVercelOrg === 'true'
            ? `/account/vercel/cluster/create${search}`
            : `/account/vercel/cluster/link${search}&username=${appUserParams.username}`
        );
      } else {
        throw new Error(
          `Missing orgId:${orgId} or vercelUserId:${vercelUserId} in Vercel integration. Please contact MongoDB customer service to help you resolve!`
        );
      }
    } catch (error) {
      sendError({ error, team: CloudTeams.CoreIam });
      setErrorMessage(error.message);
      setAcknowledgeDisabled(false);
    }
  };

  return (
    <VercelPage pageTitle="Vercel Confirmation">
      <VercelLayout
        title="Confirm Access to Your Organization "
        isForm
        formProps={{
          onSubmit: handleSubmit,
          buttonText: 'I Acknowledge',
          disabled: acknowledgeDisabled,
        }}
      >
        {errorMessage && <p css={errorMessageStyle}>{errorMessage}</p>}
        <BodyText>
          A MongoDB Atlas organization has been created. I acknowledge that I am giving Vercel access to my Atlas
          Organization.
        </BodyText>
        <GraphicContainer>
          <div>
            <GraphicLogo src="/static/images/vercel-icon.svg" alt="Vercel logo" />
            <GraphicTitle>vercel account</GraphicTitle>
            <GraphicName>&quot;{displayTeamName ?? vercelEmail}&quot;</GraphicName>
            <GraphicID>(ID: {vercelTeamId || vercelUserId})</GraphicID>
          </div>
          <GraphicArrowContainer>
            <Icon glyph="ArrowRight" size={40} />
          </GraphicArrowContainer>
          <div>
            <GraphicLogo src={mongoCircle} alt="MongoDB logo" />
            <GraphicTitle>atlas organization</GraphicTitle>
            <GraphicName>&quot;{displayOrgName}&quot;</GraphicName>
            <GraphicID>(ID: {orgId})</GraphicID>
          </div>
        </GraphicContainer>
        <BodyText>
          In order to remove this integration access to my MongoDB Atlas organization, I understand that I will need to
          manually take action to{' '}
          <a
            href="https://vercel.com/docs/integrations#installing-your-integration/managing-installed-integrations."
            target="_blank"
            rel="noreferrer"
          >
            uninstall this Vercel Integration
          </a>
          .
        </BodyText>
      </VercelLayout>
    </VercelPage>
  );
}
